import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';

import {
  Box,
  Button,
  Text,
  Checkbox,
  CardStackSection,
  DividerCard,
  Divider,
  Stack,
  Heading,
  TextField,
  Radio,
  TextLink,
} from '@origin-digital/ods-core';
import {
  IPaymentCardValues,
  PaymentCard,
  PaymentSelector,
  ADD_CARD_IDX,
  IPartialCard,
} from '@origin-digital/payments';
import { required, minAmount } from '@origin-digital/validation';
import { formatAmount, formatCurrency } from '@origin-digital/formatters';
import { onlyCurrency } from '@origin-digital/normalize';
import { MastercardBlackIcon, VisaBlackIcon } from '@origin-digital/icon';
import { navTo } from '@origin-digital/event-dispatcher';
import { ICardDTO } from '@origin-digital/pmservice-dapi-contract';

import { getCards } from 'src/services';
import { routes } from 'src/consts';
import { useAppContext } from 'src/context/AppContext';
import { useSharedAppContext } from 'src/context/SharedAppContext';
import { useTextFieldState, usePaymentCardState, useOnce } from 'src/hooks';
import { Form } from 'src/components/Form';
import { Loading } from 'src/components/Loading';
import { ContractInformation } from 'src/components/ContractInformation';
import { reduceValidators, isMyAccount, isLpg, appendInvalidRedirectUrl } from 'src/utils/helpers';
import { showErrorNotification } from 'src/utils/notification';
import { formStart, formUpdate } from 'src/utils/tracking';

import * as content from './content.json';

const IconWrapper = styled(Box)`
  display: inline-flex;
  vertical-align: top;
  margin-left: 8px;

  > svg {
    margin-left: 4px;
  }
`;

export const SelectPaymentMethodCard: React.SFC = () => {
  const { context, setContext } = useAppContext();
  const sharedAppContext = useSharedAppContext();
  const [redirecting, setRedirecting] = useState(false);
  const [mounting, setMounting] = useState(true);

  const amountState = useTextFieldState(context.amountProvided, '');
  const paymentCardState = usePaymentCardState(context.paymentData);

  const [paymentMethods, setPaymentMethods] = useState<ICardDTO[]>([]);

  const hasPaymentMethods = () => !!paymentMethods.length;
  const hasSelectedCard = () => context.paymentMethodSelectedIdx >= 0;
  const hasSelectedAddCard = () => context.paymentMethodSelectedIdx === ADD_CARD_IDX;

  const shouldShowPaymentSelector = () =>
    isMyAccount() && hasPaymentMethods() && !isLpg(context.divisionId);
  const shouldShowPaymentCard = () =>
    !isMyAccount() || !hasPaymentMethods() || hasSelectedAddCard();
  const shouldShowSavedPaymentCards = () =>
    isMyAccount() && shouldShowPaymentCard() && !isLpg(context.divisionId);
  const isLPGUnAuthenticated = () => !isMyAccount() && isLpg(context.divisionId);
  const hasLPGOrderNumber = () => isLpg(context.divisionId) && context.orderNumber;
  const showFixedAmount = () => {
    /* 
      1. isLPG and not isMyAccount - Show fixed amount
      2. isLPG and isMyAccount and hasOrderNumber - Show fixed amount
      3. isLPG and isMyAccount and not hasOrderNumber - Show other amount
    */

    return (
      (isLpg(context.divisionId) && context.orderNumber && isMyAccount()) ||
      (isLpg(context.divisionId) && !isMyAccount())
    );
  };

  useOnce(async () => {
    if (!isMyAccount() || !shouldShowSavedPaymentCards()) {
      return setMounting(false);
    }

    const cards = await getCards();

    setPaymentMethods(cards);
    setMounting(false);
  });

  const handleValidate = (): string => {
    if (context.payAmountDue || isLpg(context.divisionId)) {
      return '';
    }

    if (amountState.value !== '') {
      amountState.setValue(formatAmount(parseFloat(amountState.value.replace(/,/g, '') || '0')));
    }

    const { errors } = content.form.fields.amount;

    const validators = [
      required({ error: errors.required }),
      minAmount({ error: errors.minAmount, amount: 1 }),
    ];

    const errorMessage = reduceValidators(amountState.value, validators);

    amountState.setError(errorMessage);

    return errorMessage;
  };

  const handleSaveCard = () => {
    formStart({ context, setContext }, sharedAppContext);
    setContext({
      saveCard: !context.saveCard,
      started: true,
    });
  };

  const hasFormErrors = (): boolean => {
    if (!!handleValidate()) {
      return true;
    }

    if (isMyAccount()) {
      if (hasSelectedCard()) {
        return false;
      }

      if (hasPaymentMethods() && !hasSelectedCard() && !hasSelectedAddCard()) {
        return true;
      }
    }

    if (!paymentCardState.errors && !paymentCardState.values) {
      return true;
    }

    return (
      (paymentCardState.errors &&
        Object.values(paymentCardState.errors).some((value) => !!value)) ||
      !Object.values(paymentCardState.values).every((value) => !!value)
    );
  };

  const handleAmountPreferenceChange = (payAmountDue: boolean) => () => {
    formStart({ context, setContext }, sharedAppContext);
    setContext({
      payAmountDue,
      amount: payAmountDue ? context.amountDue : amountState.value,
      started: true,
    });
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formStart({ context, setContext }, sharedAppContext);

    amountState.setValue(onlyCurrency(event.target.value, ''));
  };

  const handlePaymentMethodChange = (paymentMethodSelectedIdx: number) => {
    formStart({ context, setContext }, sharedAppContext);

    setContext({
      paymentMethodSelectedIdx,
      paymentMethod: paymentMethods[paymentMethodSelectedIdx],
      started: true,
    });
  };

  const handlePaymentCardChange = (values: IPaymentCardValues) => {
    formStart({ context, setContext }, sharedAppContext);

    paymentCardState.setValues(values);
  };

  const handlePaymentCardExpiryChange = async ({ paymentCardId: cardId }: IPartialCard) => {
    formStart({ context, setContext }, sharedAppContext);
    try {
      await navTo.paymentMethodEditCard({
        midflow: true,
        cardId,
      });

      setMounting(true);

      const cards = await getCards();

      setPaymentMethods(cards);
      setMounting(false);
    } catch (_) {
      // We don't actually have to handle this error as it only
      // happens if a customer cancels out of a update expiry change
    }
  };

  const handleSubmit = (): void => {
    const { divisionId, amountDue, payAmountDue } = context;

    formStart({ context, setContext }, sharedAppContext);

    paymentCardState.setForceValidation(true);

    if (hasFormErrors()) {
      if (isMyAccount() && context.paymentMethod === undefined) {
        showErrorNotification(content.form.errors.paymentMethod);
      }

      return;
    }

    const amount =
      payAmountDue || isLPGUnAuthenticated() || hasLPGOrderNumber() ? amountDue : amountState.value;

    formUpdate({
      amount,
      amountDue,
      divisionId,
    });

    setContext({
      paymentData: paymentCardState.values,
      amount,
      amountProvided: amountState.value,
    });

    setRedirecting(true);
  };

  if (mounting) {
    return <Loading />;
  }

  if (redirecting) {
    return (
      <div data-id="redirect-confirm">
        <Redirect to={appendInvalidRedirectUrl(routes.confirm)} push />
      </div>
    );
  }

  return (
    <CardStackSection data-id="select-payment-method">
      <DividerCard space={['large', 'xlarge']}>
        <ContractInformation
          address={context.address}
          divisionId={context.divisionId}
          contractAccountId={context.contractAccountId}
        />

        <Form onSubmit={handleSubmit}>
          <Stack space={['large', 'xlarge']}>
            <Heading variant="h4">{content.heading}</Heading>

            {showFixedAmount() ? (
              <Stack space={['medium', 'large']}>
                <Stack space={'xsmall'}>
                  <Text variant="body-small" tone="neutralLight">
                    {content.form.fields.amount.label}
                  </Text>
                  <Heading variant="h2" data-id="amount-description">
                    {formatCurrency(parseFloat(context.amount))}
                  </Heading>
                </Stack>
                <Divider />
              </Stack>
            ) : (
              <Stack space={['large', 'xlarge']}>
                {!context.showAmountDue ? (
                  <TextField
                    id="amount"
                    label={content.form.fields.amount.label}
                    name="amount"
                    value={amountState.value}
                    error={amountState.error}
                    helperText={amountState.helperText}
                    type="text"
                    maxLength={12}
                    onChange={handleAmountChange}
                    onBlur={handleValidate}
                    domProps={{
                      pattern: '[0-9].*',
                      inputMode: 'decimal',
                      autoFocus: true,
                    }}
                  />
                ) : (
                  <Stack space={['large', 'xlarge']}>
                    <Stack space="xsmall">
                      <Radio
                        label={content.preferredAmount.full}
                        checked={context.payAmountDue}
                        onChange={handleAmountPreferenceChange(true)}
                        id="amount-preference-full"
                      />

                      <Box marginLeft="xlarge">
                        {context.payAmountDue ? (
                          <Heading variant="h2" data-id="amount-due">
                            {formatCurrency(parseFloat(context.amountDue))}
                          </Heading>
                        ) : (
                          <Text variant="subtitle" tone="neutralDark" data-id="amount-description">
                            {formatCurrency(parseFloat(context.amountDue))}
                          </Text>
                        )}
                      </Box>
                    </Stack>

                    <Stack space="xsmall">
                      <Radio
                        label={content.preferredAmount.other}
                        checked={!context.payAmountDue}
                        onChange={handleAmountPreferenceChange(false)}
                        id="amount-preference-other"
                        data-id="amount-preference-other"
                      />

                      {!context.payAmountDue && (
                        <Box marginLeft="xlarge">
                          <TextField
                            id="amount"
                            label={content.form.fields.amount.label}
                            name="amount"
                            value={amountState.value}
                            error={amountState.error}
                            helperText={amountState.helperText}
                            type="text"
                            maxLength={12}
                            onChange={handleAmountChange}
                            onBlur={handleValidate}
                            domProps={{
                              pattern: '[0-9].*',
                              inputMode: 'decimal',
                            }}
                          />
                        </Box>
                      )}
                    </Stack>
                  </Stack>
                )}

                <Divider />
              </Stack>
            )}

            <Heading variant="h4">
              {content.form.heading}

              <IconWrapper>
                <MastercardBlackIcon />
                <VisaBlackIcon />
              </IconWrapper>
            </Heading>

            {shouldShowPaymentSelector() && (
              <PaymentSelector
                hideAddBankAccount
                addCardText={content.form.fields.card.label}
                paymentMethods={paymentMethods}
                selectedIdx={context.paymentMethodSelectedIdx}
                onChange={handlePaymentMethodChange}
                onUpdateCard={handlePaymentCardExpiryChange}
              />
            )}

            {shouldShowPaymentCard() && (
              <PaymentCard
                values={paymentCardState.values}
                errors={paymentCardState.errors}
                onValidate={paymentCardState.setErrors}
                onChange={handlePaymentCardChange}
                forceValidation={paymentCardState.forceValidation}
                autoFocus={isLPGUnAuthenticated()}
                showIcon
              />
            )}

            {shouldShowSavedPaymentCards() && <Divider />}

            {shouldShowSavedPaymentCards() && (
              <Checkbox
                checked={context.saveCard}
                onChange={handleSaveCard}
                id="enter-new-card:save-this-card"
                data-id="enter-new-card:save-this-card"
                label={content.form.save}
              />
            )}

            <Divider />

            <Text>
              <TextLink href={content.cardFee.href} target="_blank" data-id="card-fee">
                {content.cardFee.text}
              </TextLink>
            </Text>

            <Button data-id="continue-button" type="submit" fullWidth>
              {content.form.button}
            </Button>
          </Stack>
        </Form>
      </DividerCard>
    </CardStackSection>
  );
};
